import {Box} from '@mantine/core';
import type React from 'react';
import type {
  RecruitmentDetailSchedule,
  RecruitmentSchedulesScheme,
} from '../../../../../core/entities/recruitment/schema';
import {toJstDate} from '../../../../../shared/services/dateUtils';
import {getDeadlineCountdown} from '../../../usecase/getDeadlineCountdown';
import {getDeadlineStatus} from '../../../usecase/getDeadlineStatus';

type Props = {
  schedule: RecruitmentSchedulesScheme | RecruitmentDetailSchedule;
  layout?: 'default' | 'text';
  /** 締切までの残り時間を出すか（layout="text" のみ有効） */
  withCountdownBadge?: boolean;
};

export const Deadline: React.FC<Props> = ({
  schedule,
  layout = 'default',
  withCountdownBadge = false,
}) => {
  if (!schedule.entryEnd) {
    return null;
  }

  const now = new Date();
  const entryEndDate = new Date(schedule.entryEnd);

  // 締切が過ぎている場合は表示しない
  // NULLの代わりに0001-01-01 00:00:00が入っている場合もこちらで除外する
  if (now > entryEndDate) {
    return null;
  }

  // 表示はJST
  const jstNow = toJstDate(now);
  const jstEnd = toJstDate(entryEndDate);
  const deadlineStatus = getDeadlineStatus(entryEndDate);
  const year = jstEnd.getFullYear().toString().slice(-2);
  const shownYear = year !== jstNow.getFullYear().toString().slice(-2);
  const month = jstEnd.getMonth() + 1;
  const day = jstEnd.getDate();
  const dayOfWeek = jstEnd.toLocaleDateString('ja-JP', {
    weekday: 'short',
  });
  const time = jstEnd.toLocaleTimeString('ja-JP', {
    hour: '2-digit',
    minute: '2-digit',
  });

  // layout="text"の場合はシンプルなテキスト表示
  if (layout === 'text') {
    const dateText = `${shownYear ? `${year}/` : ''}${month}/${day}(${dayOfWeek})`;

    // 残り時間を出さない画面は従来の表示のまま
    if (!withCountdownBadge) {
      if (deadlineStatus.isHighlight) {
        return (
          <div className="flex text-right items-center gap-8">
            <div className="bg-deadline-notification rounded-full text-white py-[2px] px-8 text-xs">
              締切
            </div>
            <div className="text-deadline-notification text-sm font-bold">
              {deadlineStatus.text} {time}
            </div>
          </div>
        );
      }

      return (
        <div className="flex text-right items-center gap-8">
          <div className="bg-deadline-primary rounded-full text-white py-[2px] px-8 text-sm">
            締切
          </div>
          <div className="text-base font-bold text-black-1">{`${dateText} ${time}`}</div>
        </div>
      );
    }

    const countdown = getDeadlineCountdown(entryEndDate);
    const hasCountdown = countdown.type !== 'none';
    // 残り日数を出すときは「明日 / 明後日」ではなく締切日そのものを見せる
    const dateLabel =
      countdown.type === 'hours' && deadlineStatus.isHighlight
        ? deadlineStatus.text
        : dateText;

    return (
      <div className="flex text-right items-center gap-8">
        {/* 塗りの矩形は幾何中心で揃うが文字は字面が上寄りになるため1px上げて合わせる */}
        <div
          className={`relative top-[-1px] flex-shrink-0 rounded-full text-white py-[2px] px-8 ${
            hasCountdown
              ? 'bg-deadline-notification text-xs'
              : 'bg-deadline-primary text-sm'
          }`}
        >
          締切
        </div>
        {hasCountdown && (
          <div className="flex-shrink-0 whitespace-nowrap text-base font-bold text-black-1">
            あと
            <span className="text-deadline-notification">
              {/* 行の高さを押し上げず、他のテキストと同じベースラインの上にはみ出させる */}
              <span className="px-4 text-[24px] leading-[0]">
                {countdown.value}
              </span>
              {countdown.unit}
            </span>
          </div>
        )}
        <div className="text-base font-bold text-black-2">{`${dateLabel} ${time}`}</div>
      </div>
    );
  }

  const getStatusContent = () => {
    if (deadlineStatus.isHighlight) {
      return (
        <Box className="w-full text-white bg-deadline-notifcation border-t-[1px] border-t-white pc:rounded-b-[6px] rounded-b-4">
          <Box className="">
            <span className="text-base font-bold">{deadlineStatus.text}</span>
            <span className="text-xs ml-4">{time}</span>
          </Box>
        </Box>
      );
    }

    return (
      <Box className="w-full text-deadline-primary bg-white border-[1px] border-t-white pc:rounded-b-[6px] rounded-b-4">
        <Box className="flex justify-center items-baseline">
          <span className="text-base font-bold">
            {shownYear && `${year}/`}
            {month}/{day}
          </span>
          <span className="text-xs font-bold ml-[2px] mt-[-4px]">
            ({dayOfWeek})
          </span>
          <span className="text-xs ml-4">{time}</span>
        </Box>
      </Box>
    );
  };

  const baseClasses = 'flex flex-col text-center justify-center items-center';
  const roundedClasses = 'pc:rounded-[6px] rounded-4';
  const highlightClass = deadlineStatus.isHighlight
    ? 'bg-deadline-notification'
    : 'bg-deadline-primary';
  const widthClass = shownYear
    ? 'pc:w-[140px] w-[140px]'
    : 'pc:w-[110px] w-[110px]';

  return (
    <Box
      key={`schedule-${schedule.id}`}
      className={`${baseClasses} ${roundedClasses} ${highlightClass} ${widthClass}`}
    >
      <Box className="w-full text-white text-xs px-12 py-[1px]">締切</Box>
      {getStatusContent()}
    </Box>
  );
};
